<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';

	import { Button, Icon, Text } from '@Shelly/shelly-design-system';

	import { user_data, user_settings } from 'src/stores/account/user';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		CAMERA_CLOUD_STORAGE_GRADIENT_START,
		CAMERA_CLOUD_STORAGE_SCROLL_PARAM,
		hydrateFromUserSettings,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { CameraCloudStorageSubscriptionStatus } from 'src/types/CameraCloudStorage';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { isRepoUser } from 'src/helpers/user_server_conditions';
	import ManageCameraCloudStorageModal from './ManageCameraCloudStorageModal.svelte';

	export let addMarginTop = false;
	/** Device ID of the camera currently shown in the side panel / drawer. */
	export let deviceId: string | undefined;

	let showManageModal = false;

	onMount(() => hydrateFromUserSettings($user_settings));

	$: isPremium = ($user_data?.premium ?? 0) > 0;
	$: activePlan = $camera_cloud_storage.activePlan;
	$: hasActiveCcs = !!activePlan && activePlan.status === CameraCloudStorageSubscriptionStatus.Active;
	$: slotAssignments = $camera_cloud_storage.slotAssignments;
	$: anySlotAssigned = Object.values(slotAssignments ?? {}).some(Boolean);

	$: variant = isPremium || hasActiveCcs ? 'enable-premium' : 'subscribe';
	$: shouldShow = !anySlotAssigned;

	const navigateToSubscriptions = () => {
		push(`/settings/premium?${CAMERA_CLOUD_STORAGE_SCROLL_PARAM}=1`);
		resetDrawerClose();
	};

	const handleClick = () => {
		if (variant === 'enable-premium' && deviceId) {
			showManageModal = true;
		} else {
			navigateToSubscriptions();
		}
	};
</script>

{#if isRepoUser()}
	{#if shouldShow}
		<div class={`ccs-promo-banner ${$showNewLayout || addMarginTop ? 'mt-m' : ''}`}>
			<div class="d-flex align-items-start gap-s mb-s">
				{#if variant === 'subscribe'}
					<Icon
						icon="fas fa-camera"
						size="m"
						style={`color: ${CAMERA_CLOUD_STORAGE_GRADIENT_START};`}
					/>
					<Text styleType="body-small" class="ccs-promo-text">
						{$format('promo_banner_subscribe_now_text', {
							default:
								'Unlock cloud storage for your cameras with our Premium or Camera Storage Subscription.',
						})}
					</Text>
				{:else}
					<Icon icon="fas fa-gem" size="m" class="icon-premium-color" />
					<Text styleType="body-small" class="ccs-promo-text">
						{$format('promo_banner_enable_cloud_storage_text', {
							default:
								'Enable cloud storage by assigning your camera to one of the available cloud storage slots.',
						})}
					</Text>
				{/if}
			</div>

			{#if variant === 'subscribe'}
				<Button
					class="ccs-plan-button ccs-plan-button--purple ccs-promo-button"
					size="m"
					variant="secondary"
					on:click={handleClick}
				>
					{$format('button_promo_subscribe_now', { default: 'Subscribe now' })}
				</Button>
			{:else}
				<Button class="ccs-promo-button" size="m" variant="premium" on:click={handleClick}>
					{$format('button_promo_enable_cloud_storage', {
						default: 'Enable Cloud Storage',
					})}
				</Button>
			{/if}
		</div>
	{/if}
{/if}

{#if showManageModal}
	<ManageCameraCloudStorageModal
		bind:showModal={showManageModal}
		{activePlan}
		preselectedCameraId={deviceId}
		on:close={() => (showManageModal = false)}
		on:upgrade={() => {
			showManageModal = false;
			navigateToSubscriptions();
		}}
	/>
{/if}

<style>
	.ccs-promo-banner {
		border-radius: 12px;
		background-color: var(--color-bg-2, rgba(255, 255, 255, 0.04));
		padding: 12px;
	}

	.ccs-promo-banner :global(.ccs-promo-text) {
		line-height: 1.4;
	}

	.ccs-promo-banner :global(.ccs-promo-button) {
		width: auto;
	}
</style>
